import { useState, useEffect } from 'react';
import {
    TextField,
    Autocomplete,
    Stack,
    Paper,
    AutocompleteRenderInputParams,
} from '@mui/material';
import { useFormContext, FieldErrors } from 'react-hook-form';
import {
    Address,
    type AddressAutocompleteSuggestion,
    useAddressAutocomplete,
} from '@wwf/sdk';

/**
 * Custom address autofill component that integrates Geoapify Address Autocomplete
 * with React Admin's form system using react-hook-form.
 * Uses Geoapify's REST API for address suggestions.
 */
export const AddressAutofillInputs = () => {
    const { setValue, watch } = useFormContext();
    const { suggestions, loading, search } = useAddressAutocomplete({
        minLength: 3,
        limit: 10,
    });
    const [streetValue, setStreetValue] = useState<string | null>(null);
    const [open, setOpen] = useState(false);

    // Watch the address object to sync with form state
    const address = (watch('address') || {}) as Address;

    // Initialize street value from address object
    useEffect(() => {
        if (address?.street && !streetValue) {
            setStreetValue(address.street);
        }
    }, [address?.street, streetValue]);

    // Auto-open/close autocomplete based on suggestions
    useEffect(() => {
        if (suggestions.length > 0) {
            setOpen(true);
        } else {
            setOpen(false);
        }
    }, [suggestions]);

    // Handle street input changes and fetch suggestions from Geoapify
    const handleStreetChange = (value: string) => {
        setStreetValue(value);
        setValue(
            'address',
            {
                ...address,
                street: value,
            },
            { shouldValidate: true, shouldDirty: true }
        );
        search(value);
    };

    // Handle suggestion selection
    const handleSuggestionSelect = (
        suggestion: AddressAutocompleteSuggestion | null
    ) => {
        if (!suggestion) {
            setOpen(false);
            return;
        }

        const result = suggestion.result;

        // Extract address components from Geoapify response
        const street = [
            result.name,
            result.street || result.address_line1 || null,
        ]
            .filter(Boolean)
            .join(', ');
        const cityName = result.city || null;
        const postalCodeValue = result.postcode || null;
        const countryCode = result.country_code?.toUpperCase() || null;
        const countryName = result.country || null;

        // Extract coordinates
        const lng = result.lon;
        const lat = result.lat;

        // Build the complete address object
        const addressObj: Address = {
            ...address,
            street: street,
            postal_code: postalCodeValue,
            city: cityName,
            country: countryName || countryCode,
            coordinates:
                lat !== undefined && lng !== undefined
                    ? {
                          latitude: lat,
                          longitude: lng,
                      }
                    : address.coordinates || null,
        };

        // Update form with the complete address object
        setValue('address', addressObj, {
            shouldValidate: true,
            shouldDirty: true,
        });

        setStreetValue(street || null);
        setOpen(false);
    };

    return (
        <Stack gap={2}>
            <Autocomplete
                open={open}
                onOpen={() => {
                    if (suggestions.length > 0) {
                        setOpen(true);
                    }
                }}
                onClose={() => setOpen(false)}
                options={suggestions}
                getOptionLabel={option =>
                    typeof option === 'string' ? option : option.placeName || ''
                }
                isOptionEqualToValue={(option, value) =>
                    option.placeName === value.placeName
                }
                filterOptions={x => x}
                freeSolo={false}
                inputValue={streetValue || ''}
                onInputChange={(_, newInputValue, reason) => {
                    // Only handle input changes, not selection changes
                    if (reason === 'input') {
                        handleStreetChange(newInputValue);
                    }
                }}
                onChange={(_, newValue) => {
                    handleSuggestionSelect(
                        newValue as AddressAutocompleteSuggestion | null
                    );
                }}
                renderInput={params => <AddressStreetInput {...params} />}
                PaperComponent={props => <Paper {...props} sx={{ mt: 1 }} />}
                noOptionsText={
                    suggestions.length === 0 &&
                    streetValue &&
                    streetValue.length >= 3 &&
                    !loading
                        ? 'No addresses found'
                        : 'Start typing an address...'
                }
                loading={loading}
            />
            <AddressPostalCodeInput />
            <AddressCityInput />
            <AddressCountryInput />
            <AddressLatitudeInput />
            <AddressLongitudeInput />
        </Stack>
    );
};

// Individual input components that work with React Admin
const AddressStreetInput = (params: AutocompleteRenderInputParams) => {
    const {
        watch,
        setValue,
        formState: { errors },
    } = useFormContext();
    const address = (watch('address') || {}) as Address;
    const error = errors.address as
        | FieldErrors<{ address: Address }>['address']
        | undefined;

    return (
        <TextField
            {...params}
            value={address.street || ''}
            onChange={e => {
                setValue(
                    'address',
                    {
                        ...address,
                        street: e.target.value,
                    },
                    { shouldValidate: true, shouldDirty: true }
                );
            }}
            label="Street"
            fullWidth
            error={!!error?.street}
            helperText={error?.street?.message || false}
            inputProps={{
                ...params.inputProps,
                autoComplete: 'address-line1',
            }}
        />
    );
};

const AddressPostalCodeInput = () => {
    const {
        watch,
        setValue,
        formState: { errors },
    } = useFormContext();
    const address = (watch('address') || {}) as Address;
    const error = errors.address as
        | FieldErrors<{ address: Address }>['address']
        | undefined;

    return (
        <TextField
            value={address.postal_code || ''}
            onChange={e => {
                setValue(
                    'address',
                    {
                        ...address,
                        postalCode: e.target.value,
                    },
                    { shouldValidate: true, shouldDirty: true }
                );
            }}
            label="Postal Code"
            fullWidth
            error={!!error?.postal_code}
            helperText={error?.postal_code?.message || false}
            inputProps={{
                autoComplete: 'postal-code',
            }}
        />
    );
};

const AddressCityInput = () => {
    const {
        watch,
        setValue,
        formState: { errors },
    } = useFormContext();
    const address = (watch('address') || {}) as Address;
    const error = errors.address as
        | FieldErrors<{ address: Address }>['address']
        | undefined;

    return (
        <TextField
            value={address.city || ''}
            onChange={e => {
                setValue(
                    'address',
                    {
                        ...address,
                        city: e.target.value,
                    },
                    { shouldValidate: true, shouldDirty: true }
                );
            }}
            label="City"
            fullWidth
            error={!!error?.city}
            helperText={error?.city?.message || false}
            inputProps={{
                autoComplete: 'address-level2',
            }}
        />
    );
};

const AddressCountryInput = () => {
    const {
        watch,
        setValue,
        formState: { errors },
    } = useFormContext();
    const address = (watch('address') || {}) as Address;
    const error = errors.address as
        | FieldErrors<{ address: Address }>['address']
        | undefined;

    return (
        <TextField
            value={address.country || ''}
            onChange={e => {
                setValue(
                    'address',
                    {
                        ...address,
                        country: e.target.value,
                    },
                    { shouldValidate: true, shouldDirty: true }
                );
            }}
            label="Country"
            fullWidth
            error={!!error?.country}
            helperText={error?.country?.message || false}
            inputProps={{
                autoComplete: 'country-name',
            }}
        />
    );
};

const AddressLatitudeInput = () => {
    const {
        watch,
        setValue,
        formState: { errors },
    } = useFormContext();
    const address = (watch('address') || {}) as Address;
    const coordinates = address.coordinates || {
        latitude: null,
        longitude: null,
    };
    const error = errors.address as
        | FieldErrors<{ address: Address }>['address']
        | undefined;

    return (
        <TextField
            value={coordinates.latitude || ''}
            onChange={e => {
                setValue(
                    'address',
                    {
                        ...address,
                        coordinates: {
                            ...coordinates,
                            latitude: e.target.value,
                        },
                    },
                    { shouldValidate: true, shouldDirty: true }
                );
            }}
            label="Latitude"
            fullWidth
            error={!!error?.coordinates?.latitude}
            helperText={error?.coordinates?.latitude?.message || false}
            type="number"
            inputProps={{
                step: 'any',
            }}
        />
    );
};

const AddressLongitudeInput = () => {
    const {
        watch,
        setValue,
        formState: { errors },
    } = useFormContext();
    const address = (watch('address') || {}) as Address;
    const coordinates = address.coordinates || {
        latitude: null,
        longitude: null,
    };
    const error = errors.address as
        | FieldErrors<{ address: Address }>['address']
        | undefined;

    return (
        <TextField
            value={coordinates.longitude || ''}
            onChange={e => {
                setValue(
                    'address',
                    {
                        ...address,
                        coordinates: {
                            ...coordinates,
                            longitude: e.target.value,
                        },
                    },
                    { shouldValidate: true, shouldDirty: true }
                );
            }}
            label="Longitude"
            fullWidth
            error={!!error?.coordinates?.longitude}
            helperText={error?.coordinates?.longitude?.message || false}
            type="number"
            inputProps={{
                step: 'any',
            }}
        />
    );
};
